import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { postJsonRequest } from '../../../service/apiService';
import { apiUrl } from '../../../Constant/apiConstant';

interface JwtPayload {
  exp: number;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [isOTPVerified, setIsOTPVerified] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const { tenant } = useParams<{ tenant: string }>();
  const location = useLocation();
  const [isCandidateTokenExpired, setIsCandidateTokenExpired] = useState(false);

  // Candidate session (single source of truth)
  const sessionData = sessionStorage.getItem("candidate_session");
  const session = sessionData ? JSON.parse(sessionData) : null;

  // Refresh access token if needed
  const refreshToken = async () => {
    try {
      const response = await postJsonRequest(apiUrl.ACM_POST_REFRESH_TOKEN, {}, tenant, true);
      if (response.status === 200 && response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        sessionStorage.removeItem('candidate_session')
        setToken(response.data.access_token);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('access_token');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error("❌ Error refreshing token", error);
      localStorage.removeItem('access_token');
      setIsAuthenticated(false);
    }
  };

  // Check access_token expiry
  useEffect(() => {
    if (!token) {
      setIsAuthenticated(false);
      return;
    }
    try {
      const decodedToken = jwtDecode<JwtPayload>(token);
      const currentTime = Date.now() / 1000;
      const timeToExpire = decodedToken.exp - currentTime;

      if (timeToExpire <= 0) {
        localStorage.removeItem('access_token');
        setIsAuthenticated(false);
      } else if (timeToExpire <= 300) {
        refreshToken();
      } else {
        const timeoutDuration = (timeToExpire - 300) * 1000;
        const refreshTimeout = setTimeout(() => {
          refreshToken();
        }, timeoutDuration);
        return () => clearTimeout(refreshTimeout);
      }
    } catch (error) {
      console.error("❌ Invalid access_token", error);
      localStorage.removeItem('access_token');
      setIsAuthenticated(false);
    }
  }, [token, tenant]);

  // Check candidate_access_token expiry and OTP verification
  useEffect(() => {
    if (!session?.candidate_access_token) {
      setIsOTPVerified(false);
      setIsCandidateTokenExpired(false);
      return;
    }

    try {
      const decodedCandidateToken = jwtDecode<any>(session.candidate_access_token);
      const otpVerified = decodedCandidateToken["OTPVerified"];
      const currentTime = Date.now() / 1000;
      const exp = decodedCandidateToken.exp;


      setIsOTPVerified(!!otpVerified);

      if (exp < currentTime) {
        sessionStorage.removeItem('candidate_session');
        setIsCandidateTokenExpired(true);
        setIsOTPVerified(false);
      } else {
        setIsCandidateTokenExpired(false);

        const timeoutDuration = (exp - currentTime) * 1000;
        const logoutTimer = setTimeout(() => {
          sessionStorage.removeItem('candidate_session');
          setIsCandidateTokenExpired(true);
          setIsOTPVerified(false);
        }, timeoutDuration);
        return () => clearTimeout(logoutTimer);
      }
    } catch (error) {
      console.error("❌ Invalid candidate_access_token", error);
      setIsOTPVerified(false);
      setIsCandidateTokenExpired(false);
    }
  }, [session?.candidate_access_token]);

  const allowedRoutes = [
    `/${tenant}/preboarding/all-candidates/candidate-preboarding`,
    `/${tenant}/preboarding/all-candidates/candidate-preboarding/candidate-offer`,
    `/${tenant}/preboarding/all-candidates/candidate-preboarding/candidate-details`,
    `/${tenant}/preboarding/all-candidates/candidate-preboarding/documents`,
    `/${tenant}/preboarding/all-candidates/candidate-preboarding/hiring-experience`,
    `/${tenant}/Declined`,
  ];

  const isAllowedRoute = allowedRoutes.includes(location.pathname);

  if (isCandidateTokenExpired) {
    return <Navigate to={`/${tenant}/SessionExpired`} />;
  }

  if (!isAuthenticated && !isOTPVerified) {
    // Candidate flow: allow OTPVerification page without redirecting to SignIn
    if (location.pathname === `/${tenant}/OTPVerification`) {
      return <>{children}</>;
    }
    // Otherwise → user must sign in
    return <Navigate to={`/${tenant}/SignIn`} />;
  }

  if (!token && isOTPVerified && isAllowedRoute) {
    return <>{children}</>;
  }

  if (!token && isOTPVerified && !isAllowedRoute) {
    return <Navigate to={`/${tenant}/AccessDenied`} />;
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return <Navigate to={`/${tenant}/SignIn`} />;
};

export default ProtectedRoute;